import React, { useState, useContext } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

const API_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

const PORTAL_POINTS = [
  'Connect your WhatsApp Business numbers and keep them in one place.',
  'See delivery rates and message categories for each number.',
  'Review your message templates and their approval status with Meta.'
];

const Login = () => {
  const navigate = useNavigate();
  const { login, completeTwoFactor, signIn } = useContext(AuthContext);
  // Second step when two-step verification is on
  const [twoFactorToken, setTwoFactorToken] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  // Required setup for administrators: { qrCode, secret, account } then the result with recovery codes
  const [setup, setSetup] = useState(null);
  const [setupDone, setSetupDone] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { email, password } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const success = await login(email, password);

      if (success?.setupRequired) {
        const response = await axios.post(`${API_URL}/auth/login/2fa/setup`, { twoFactorToken: success.twoFactorToken });
        setSetup(response.data.data);
        setTwoFactorToken(success.twoFactorToken);
        setCode('');
        setLoading(false);
      } else if (success?.twoFactorToken) {
        setTwoFactorToken(success.twoFactorToken);
        setCode('');
        setLoading(false);
      } else if (success) {
        navigate('/dashboard');
      } else {
        setError('The email or password is incorrect. Check them and try again.');
        setLoading(false);
      }
    } catch (err) {
      if (!err.response) {
        setError('Could not reach the server. Check your connection and try again.');
      } else if ([400, 401].includes(err.response.status)) {
        setError('The email or password is incorrect. Check them and try again.');
      } else {
        setError(err.response.data?.error || 'Sign-in failed. Try again in a moment.');
      }
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const result = await completeTwoFactor(twoFactorToken, code.trim());
      // After a recovery code, go to the profile where new codes can be created
      if (result.recoveryCodesLeft !== undefined) {
        navigate('/profile', { state: { recoveryCodesLeft: result.recoveryCodesLeft } });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.expired) {
        setTwoFactorToken(null);
        setFormData({ ...formData, password: '' });
      }
      setError(!err.response ? 'Could not reach the server. Check your connection and try again.' : (data?.error || 'Sign-in failed. Try again.'));
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/auth/login/2fa/enable`, { twoFactorToken, code });
      setSetupDone(response.data);
      setLoading(false);
    } catch (err) {
      const data = err.response?.data;
      if (data?.expired) backToPassword();
      setError(!err.response ? 'Could not reach the server. Check your connection and try again.' : (data?.error || 'That did not work. Try again.'));
      setLoading(false);
    }
  };

  const finishSetup = () => {
    signIn(setupDone);
    navigate('/dashboard');
  };

  const recoveryText = setupDone
    ? `Recovery codes for ${email} (WhatsApp API Portal)\nEach code works once.\n\n${setupDone.recoveryCodes.join('\n')}\n`
    : '';
  const downloadCodes = () => {
    const url = URL.createObjectURL(new Blob([recoveryText], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'whatsapp-portal-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const backToPassword = () => {
    setSetup(null);
    setSetupDone(null);
    setShowKey(false);
    setTwoFactorToken(null);
    setCode('');
    setUseRecovery(false);
    setError(null);
    setFormData({ ...formData, password: '' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 }
      }}
    >
      <Paper
        variant="outlined"
        component="main"
        sx={{
          width: '100%',
          maxWidth: { xs: 440, md: 920 },
          borderColor: RULE,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK }}
          >
            {setupDone ? 'Save your recovery codes' : setup ? 'Set up two-step verification' : twoFactorToken ? 'Enter your code' : 'Sign in'}
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, mb: 3 }}>
            {setupDone
              ? "If you lose your phone, sign in with one of these codes instead. Each works once. Keep them somewhere safe; they won't be shown again."
              : setup
                ? 'Administrator accounts need a code from an authenticator app (such as Google Authenticator, Microsoft Authenticator or Authy) as well as the password.'
                : !twoFactorToken
              ? 'Use the email and password for your WhatsApp API Portal account.'
              : useRecovery
                ? 'Enter one of the recovery codes you saved when you turned on two-step verification. Each code works once.'
                : `Open your authenticator app and enter the 6-digit code for ${email}.`}
          </Typography>

          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {setupDone ? (
            <Box>
              <Box
                component="ul"
                aria-label="Recovery codes"
                sx={{
                  listStyle: 'none', m: 0, p: 2, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1,
                  bgcolor: '#F6F8FA', border: `1px solid ${RULE}`, borderRadius: 1.5,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', color: INK
                }}
              >
                {setupDone.recoveryCodes.map((c) => <li key={c}>{c}</li>)}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button size="small" onClick={() => navigator.clipboard?.writeText(recoveryText).catch(() => {})} sx={{ textTransform: 'none' }}>Copy</Button>
                <Button size="small" onClick={downloadCodes} sx={{ textTransform: 'none' }}>Download</Button>
              </Box>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={finishSetup}
                sx={{ mt: 3, py: 1.25, textTransform: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: 2 }}
              >
                I've saved them, continue
              </Button>
            </Box>
          ) : setup ? (
            <Box component="form" onSubmit={confirmSetup} noValidate>
              <Box component="ol" sx={{ pl: 2.5, m: 0, color: INK, '& > li': { mb: 2 } }}>
                <li>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>In the app, add an account and scan this code.</Typography>
                  <Box component="img" src={setup.qrCode} alt="QR code to add this account to an authenticator app" sx={{ width: 170, height: 170, border: `1px solid ${RULE}`, borderRadius: 1.5, display: 'block' }} />
                  {showKey ? (
                    <Typography variant="body2" sx={{ mt: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', wordBreak: 'break-all' }}>
                      {setup.secret}
                    </Typography>
                  ) : (
                    <Link component="button" type="button" variant="body2" onClick={() => setShowKey(true)} sx={{ mt: 1, color: '#0D7332', fontWeight: 600 }}>
                      Can't scan it? Enter a key instead
                    </Link>
                  )}
                </li>
                <li>
                  <Typography variant="body2" sx={{ mb: 1 }}>Enter the 6-digit code the app shows.</Typography>
                  <TextField
                    fullWidth
                    required
                    id="setup-code"
                    label="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { letterSpacing: '0.3em', fontSize: '1.25rem' } }}
                  />
                </li>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                disabled={loading || code.length !== 6}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ mt: 1, py: 1.25, textTransform: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: 2 }}
              >
                {loading ? 'Checking' : 'Turn on and continue'}
              </Button>
              <Link component="button" type="button" variant="body2" onClick={backToPassword} sx={{ color: MUTED, mt: 2.5, display: 'block' }}>
                Back to sign in
              </Link>
            </Box>
          ) : twoFactorToken ? (
            <Box component="form" onSubmit={submitCode} noValidate>
              <TextField
                key={useRecovery ? 'recovery' : 'app'}
                margin="dense"
                required
                fullWidth
                autoFocus
                id="code"
                name="code"
                label={useRecovery ? 'Recovery code' : '6-digit code'}
                value={code}
                onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                inputProps={useRecovery
                  ? { autoCapitalize: 'none', spellCheck: false, style: { letterSpacing: '0.08em' } }
                  : { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { letterSpacing: '0.3em', fontSize: '1.25rem' } }}
                placeholder={useRecovery ? 'xxxx-xxxx' : '123456'}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                disabled={loading || (useRecovery ? code.trim().length < 8 : code.length !== 6)}
                aria-busy={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ mt: 3, py: 1.25, textTransform: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: 2 }}
              >
                {loading ? 'Checking' : 'Verify and sign in'}
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mt: 2.5 }}>
                <Link component="button" type="button" variant="body2" onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(null); }} sx={{ color: '#0D7332', fontWeight: 600 }}>
                  {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
                </Link>
                <Link component="button" type="button" variant="body2" onClick={backToPassword} sx={{ color: MUTED }}>
                  Back to sign in
                </Link>
              </Box>
              <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 2 }}>
                Lost your phone and your recovery codes? Ask your Bitvoice administrator to reset two-step verification.
              </Typography>
            </Box>
          ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="dense"
              required
              fullWidth
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleChange}
              sx={{ mb: 1.5 }}
            />
            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={loading}
              aria-busy={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ mt: 3, py: 1.25, textTransform: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: 2 }}
            >
              {loading ? 'Signing in' : 'Sign in'}
            </Button>
            <Typography variant="body2" sx={{ color: MUTED, mt: 3 }}>
              New to the portal?{' '}
              <Link component={RouterLink} to="/register" sx={{ color: '#0D7332', fontWeight: 600 }}>
                Create an account
              </Link>
            </Typography>
          </Box>
          )}
        </Box>

        <Box
          component="aside"
          aria-label="About the portal"
          sx={{
            p: { xs: 3, sm: 5 },
            bgcolor: '#F6F8FA',
            borderTop: { xs: `1px solid ${RULE}`, md: 'none' },
            borderLeft: { xs: 'none', md: `1px solid ${RULE}` },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK, mb: 0.5 }}>
            WhatsApp API Portal
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
            WhatsApp Business messaging, provided by Bitvoice.
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {PORTAL_POINTS.map((point) => (
              <Box
                component="li"
                key={point}
                sx={{ display: 'flex', gap: 1.5, py: 1.25, borderTop: `1px solid ${RULE}` }}
              >
                <Box
                  aria-hidden="true"
                  sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: 'primary.main', flexShrink: 0, mt: '7px' }}
                />
                <Typography variant="body2" sx={{ color: INK }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
